import { IS_SSR, IsDevEnv, Settings } from '@/config'
import { UserManager, User } from 'oidc-client-ts'
import { useSyncExternalStore } from 'react'
import { getOidcConfig } from './config'
import { logout } from '../api/session'

type Listener = () => void

export function authLog(log: string, data?: any) {
  console.log(`🔐 ${log}`, data)
}
export class ConnectIDAuth {
  static _instance: ConnectIDAuth | null = null

  private userManager: UserManager

  private currentUser: User | null = null

  private listeners: Set<Listener> = new Set()

  private authReady = false

  constructor() {
    const manager = IS_SSR ? null : new UserManager(getOidcConfig())

    this.userManager = manager

    if (manager) {
      manager.getUser().then(user => {
        if (user && !user.expired) {
          this.setUser(user, 'initial-get-user')
          this.setAuthReady(true)
        }
        if (!user) {
          authLog('No user on init')
          this.setAuthReady(true)
        }
      }).catch(err => {
        authLog('Failed to get user on init', err)
        logout()
      })

      manager.events.addUserLoaded((user) => this.setUser(user, 'event-user-loaded'))
      manager.events.addUserUnloaded(() => this.setUser(null, 'event-user-unloaded'))
      manager.events.addAccessTokenExpired(() => {
        this.renewTokenSilently()
      })
    }

  }

  static get instance() {
    if (!ConnectIDAuth._instance) {
      ConnectIDAuth._instance = new ConnectIDAuth()
    }

    return ConnectIDAuth._instance
  }

  private emit = () => {
    this.listeners.forEach((l) => l())
  }

  private setUser = (user: User | null, source = '') => {
    this.currentUser = user
    this.emit()
  }

  private setAuthReady = (to: boolean) => {
    this.authReady = to
    this.emit()
  }

  public subscribe(listener: Listener) {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  public getUserSnapshot = () => this.currentUser

  public getAuthReadySnapshot = () => this.authReady

  async login(): Promise<void> {
    await this.userManager.signinRedirect()
  }

  async launchAppWithSSO(appUrl: string) {
    const { accessHubAuthorizationDomain, authorizationDomain } = IsDevEnv ? Settings.ConnectID : Settings.ConnectIDProd
    const formmatedUrl = appUrl.replace(accessHubAuthorizationDomain, authorizationDomain)
    window.open(formmatedUrl, '_blank')
  }

  async handleRedirectCallback(): Promise<User | null> {
    if (IS_SSR) return

    const isErrorCallback = window.location.href?.includes('error')

    if (isErrorCallback) {

      return null
    }

    if (this.currentUser) {
      return this.currentUser
    }

    try {
      const user = await this.userManager.signinRedirectCallback()
      this.setUser(user, 'redirect-callback')
      return user
    } catch (error) {
      logger.error('Failed to get user', error)
      return null
    }
  }

  async getUser(): Promise<User | null> {
    try {
      const user = await this.userManager?.getUser()
      this.setUser(user && !user.expired ? user : null, 'get-user')
      return this.currentUser
    } catch (error) {
      authLog('Failed to get user', error)
      return null
    }
  }

  async renewTokenSilently(): Promise<User | null> {
    try {
      authLog('Renewing token silently')
      const user = await this.userManager?.signinSilent()
      authLog('Renewing token silently response', user)
      this.setUser(user, 'signin-silent')
      this.setAuthReady(true)
      return user
    } catch (error) {
      authLog('Failed to renew token silently', error)
      logout()
      return null
    }
  }

  async logout(): Promise<void> {
    return this.userManager?.signoutRedirect()
  }

  async handleSilentCallback(): Promise<void> {
    await this.userManager?.signinSilentCallback()
  }
}

export const useConnectIDAuthState = () => {
  const store = ConnectIDAuth.instance

  return useSyncExternalStore(
    store.subscribe.bind(store),
    store.getUserSnapshot.bind(store),
    () => null,
  )
}

export const useConnectIDAuthReady = () => {
  const store = ConnectIDAuth.instance

  return useSyncExternalStore(
    store.subscribe.bind(store),
    store.getAuthReadySnapshot.bind(store),
    () => false,
  )
}
